import { useQual, useStore } from 'hooks';
import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { redactQual } from './redactQual';
import { Events } from './tracking.types';
import { useTracking } from './useTracking';

/**
 * Emits the `marketing_sites` / `qual` / `loaded` event for an inbound qual, so
 * the address and `promoId` a partner sent are queryable in Snowflake rather
 * than only visible in Datadog RUM.
 *
 * This renders nothing and exists purely so the event is dispatched from inside
 * <Tracker>'s <Track> provider. `qualify.tsx` — where the qual is actually
 * decoded — is the *parent* of ProviderWrapper and therefore sits outside that
 * provider; react-tracking does not throw there, it silently falls back to
 * pushing onto `window.dataLayer`, so an event fired from the page would reach
 * GTM and never reach eventstream. Mounting here also puts it above
 * ProviderWrapper's authentication gate, which the qualify page redirects away
 * from before it resolves.
 *
 * `qualify.tsx` hands the decode over via the non-persisted
 * `pendingQualLoadedEvent` store flag, which this claims to emit exactly once.
 */
export function QualLoadedEvent(): null {
  const { trackEvent } = useTracking();
  const qual = useQual();
  const { pendingQualLoadedEvent, claimPendingQualLoadedEvent } = useStore(
    useShallow((store) => ({
      pendingQualLoadedEvent: store.pendingQualLoadedEvent,
      claimPendingQualLoadedEvent: store.claimPendingQualLoadedEvent,
    }))
  );

  useEffect(() => {
    // The subscribed value only decides whether it is worth looking; the claim
    // below is the authority. Under `reactStrictMode` this effect is invoked
    // twice from one render, so both runs see the same (stale, truthy)
    // `pendingQualLoadedEvent` — claiming against live store state is what
    // keeps the event from being emitted twice.
    if (!pendingQualLoadedEvent) {
      return;
    }

    const claimed = claimPendingQualLoadedEvent();
    if (!claimed) {
      return;
    }

    // The details are the qual itself — address, promoId and the rest — with
    // the PII fields marked redacted (see redactQual). `skip_to_plans` and
    // `force_quiz` go last so a partner-sent key can't overwrite them.
    trackEvent(Events.QUAL, 'loaded', {
      ...redactQual(qual),
      skip_to_plans: claimed.skipToPlans,
      force_quiz: claimed.forceQuiz,
    });
    // The claim above is also why the unstable deps below (`trackEvent` is a
    // fresh closure every render) are harmless: every later run finds nothing
    // to claim.
  }, [claimPendingQualLoadedEvent, pendingQualLoadedEvent, qual, trackEvent]);

  return null;
}
